<template>
  <div class="card">
    <div class="card-header">
      <h4 class="mb-0">
        {{ $t('PQTOffersView.qiymtTklifiMqaissi') }}
      </h4>
    </div>
    <div class="row pl-5 align-end">
      <b-button
        class="col-4 mr-5 ml-4"
        @click="showOnlyPriceComparison = true"
      >
        {{ $t('newRFQRequest.yalnzQiymtMqayissi') }}
      </b-button>
      <b-button
        class="col-4 ml-5"
        @click="showDetailedView = true"
      >
        {{ $t('newRFQRequest.traflMqayis') }}
      </b-button>
    </div>

    <!-- Detailed Comparison Modal -->
    <DetailedComparisonModal
      :ranked-offers="rankedOffers"
      :visible="showDetailedView"
      :offers="offers"
      :get-parsed-prices="getParsedPrices"
      :get-product="getProduct"
      :get-requirement-reply="getRequirementReply"
      :get-tech-specification="getTechSpecification"
      :get-parsed-prices-extras="getParsedPricesExtras"
      :start-conversation="startConversation"
      :export-results-detailed-price="exportResultsDetailedPrice"
      :is-photo="isPhoto"
      :request_id="request.id"
      :get-photo="getPhoto"
      @update:visible="showDetailedView = $event"
      @confirm="handleCustomOk"
      @refreshData="handleRefreshData"
    />

    <!-- Price Comparison Modal -->
    <PriceComparisonModal
      :ranked-offers="rankedOffers"
      :visible="showOnlyPriceComparison"
      :only-price-comparison-data="onlyPriceComparisonData"
      :companies="companies"
      :export-results-only-price="exportResultsOnlyPrice"
      @update:visible="showOnlyPriceComparison = $event"
      @confirm="handleCustomOk"
    />

    <div
      v-for="o in rankedOffers"
      :key="o.id"
      class="offer-card mb-4"
    >
      <hr class="dashed">
      <div class="company-info-section">
        <div class="company-details">
          <div class="company-name-container">
            <h3 class="company-name">
              {{ o.requestUser.company.name }}
              <span
                v-if="o.rejected"
                class="rejected-badge"
              >(Rədd edilib)</span>
            </h3>
            <div class="rating-container">
              <StarRatingView
                :count="o.requestUser.company.supplyRateCount"
                :total="o.requestUser.company.supplyRateTotal"
                class="enhanced-star-rating"
                @click.prevent="openCompanyRatingStatisticPopup = o.requestUser.company.id"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Product Table -->
      <div
        class="row"
        :class="o.rejected ? 'offer-rejected' : ''"
      >
        <div class="col-md-12 mm">
          <div class="product-table-container mt-3">
            <table class="table table-responsive-md table-fixed product-comparison-table">
              <thead>
                <tr>
                  <th class="product-col">
                    Məhsul adı
                  </th>
                  <th class="price-col">
                    qiyməti
                  </th>
                  <th class="vat-col">
                    ƏDV 18%
                  </th>
                  <th class="total-col">
                    Cəm
                  </th>
                  <th class="rank-col">
                    Sıra
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="item in o.products">
                  <!-- Main Product Row -->
                  <tr
                    class="main-product-row"
                    :class="{'best-rank': item.globalRank === 1, 'no-offer-row': item.noOffer}"
                  >
                    <td class="product-name">
                      <div class="product-name-container">
                        <strong>{{ item.product.name }}</strong>
                        <span
                          v-if="item.globalRank === 1"
                          class="best-price-badge"
                        >Ən yaxşı qiymət</span>
                      </div>
                    </td>
                    <td class="price-value">
                      <div class="price-container">
                        <span v-if="item.noOffer" class="no-offer-badge">Təklif yoxdur</span>
                        <template v-else>
                          <span class="price-amount">{{ item.price }} AZN</span>
                          <span class="price-quantity">x {{ item.product.count }} {{ item.product.unit }}</span>
                        </template>
                      </div>
                    </td>
                    <td
                      v-if="item.vatAmount && !item.noOffer"
                      class="vat-value"
                    >
                      <span class="vat-amount">{{ (item.vatAmount * item.product.count).toFixed(2) }} AZN</span>
                    </td>
                    <td
                      v-else
                      class="vat-value"
                    >
                      <span class="no-vat">-</span>
                    </td>
                    <td class="total-value">
                      <span v-if="item.noOffer" class="no-vat">-</span>
                      <span v-else class="total-amount">{{ ((item.price + item.vatAmount) * item.product.count).toFixed(2) }} AZN</span>
                    </td>
                    <td class="rank-value">
                      <span v-if="item.noOffer" class="no-offer-rank">-</span>
                      <span
                        v-else
                        class="rank-badge"
                        :class="getRankClass(item.globalRank)"
                      >
                        {{ item.globalRank || '-' }}
                      </span>
                    </td>
                  </tr>

                  <!-- Alternative Offer Row (only if it exists) -->
                  <tr
                    v-if="item.alternativeOffer"
                    class="alternative-row"
                    :class="{'best-rank': item.alternativeOffer.globalRank === 1}"
                  >
                    <td class="product-name alternative">
                      <div class="product-name-container">
                        <span class="alternative-label">Alternativ</span>
                        <span class="alternative-name">{{ item.alternativeOffer.name }}</span>
                        <span
                          v-if="item.alternativeOffer.globalRank === 1"
                          class="best-price-badge"
                        >Ən yaxşı qiymət</span>
                      </div>
                    </td>
                    <td class="price-value alternative">
                      <div class="price-container">
                        <span class="price-amount">{{ item.alternativeOffer.price }} AZN</span>
                        <span class="price-quantity">x {{ item.product.count }} {{ item.product.unit }}</span>
                      </div>
                    </td>
                    <td
                      v-if="item.alternativeOffer.isVatPayer"
                      class="vat-value alternative"
                    >
                      <span class="vat-amount">{{ (item.alternativeOffer.vatAmount * item.product.count).toFixed(2) }} AZN</span>
                    </td>
                    <td
                      v-else
                      class="vat-value alternative"
                    >
                      <span class="no-vat">-</span>
                    </td>
                    <td class="total-value alternative">
                      <span class="total-amount">{{ (item.alternativeOffer.total * item.product.count).toFixed(2) }} AZN</span>
                    </td>
                    <td class="rank-value alternative">
                      <span
                        class="rank-badge"
                        :class="getRankClass(item.alternativeOffer.globalRank)"
                      >
                        {{ item.alternativeOffer.globalRank || '-' }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Offer Totals -->
      <div class="offer-totals-container">
        <div class="offer-total-row">
          <div class="offer-total-label">
            <strong>Toplam (Əsas təklif):</strong>
          </div>
          <div class="offer-total-amount">
            {{ o.mainTotal }} AZN
          </div>
          <div class="offer-total-rank">
            <span
              class="total-rank-badge"
              :class="getRankClass(o.mainRank)"
            >
              {{ o.mainRank }}
            </span>
            <span class="rank-label">yer</span>
          </div>
        </div>

        <div
          v-if="o.alternativeTotal>0"
          class="offer-total-row alternative"
        >
          <div class="offer-total-label">
            <strong>Toplam (Alternativ təklif):</strong>
          </div>
          <div class="offer-total-amount">
            {{ o.alternativeTotal }} AZN
          </div>
          <div class="offer-total-rank">
            <span
              class="total-rank-badge"
              :class="getRankClass(o.alternativeRank)"
            >
              {{ o.alternativeRank }}
            </span>
            <span class="rank-label">yer</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import StarRatingView from "@/pages/scc/utils/StarRatingView.vue"
import {
  exportRequestResultDetailed,
  exportRequestResultPrice,
  startNewOfferConversation
} from "@/repositories/GeneralDataRepository";
import {mapState} from "vuex";
import {mediaBase} from "@/repositories/Repository";
import {getCompanyFormat, getPhoto,} from "@/repositories/Utils";
import DetailedComparisonModal from "@/pages/scc/request/DetailedComparisonModal.vue";
import PriceComparisonModal from "@/pages/scc/request/PriceComparisonModal.vue";

export default {
  name: 'PQTOffersView',
  components: {
    PriceComparisonModal,
    DetailedComparisonModal,
    StarRatingView,
  },
  props: {
    request: {
      type: Object,
      default() {
        return {}
      }
    },
    offers: {
      type: Array,
      default() {
        return []
      }
    },
    products: {
      type: Array,
      default() {
        return []
      }
    },
  },
  data() {
    return {
      showOnlyPriceComparison: false,
      showDetailedView: false
    }
  },
  computed: {
    ...mapState({
      token: state => state.auth.token
    }),
    onlyPriceComparisonData() {
      // Assume flatOffers is an array of objects built from all offers
      // Each flatOffer might be:
      // { productId, companyName, main: { price, vat, total }, alternative: { price, vat, total } }
      const flatOffers = this.offers.flatMap(offer => {
        const companyName = offer.requestUser.company.name;
        const parsed = this.parsePrices(offer.prices); // should return { prices: [...] }
        const prices = parsed.prices || [];
        return prices.map(item => {
          const product = this.getProductById(item.product) || {};
          const count = product.count || 1;

          // Handle noOffer case
          if (item.noOffer) {
            return {
              productId: item.product,
              companyName,
              noOffer: true,
              main: {
                price: 0,
                vat: 0,
                total: 0,
              },
              alternative: null,
            };
          }

          const mainPrice = parseFloat(item.price) || 0;
          const isVatPayer = (item.isVatPayer === 'true');
          const vatPerUnit = isVatPayer ? this.calculateVAT(mainPrice) : 0;
          let alternative = null;
          if (item.alternativeOffer) {
            const altPrice = parseFloat(item.alternativeOffer.price) || 0;
            const altVatPerUnit = isVatPayer ? this.calculateVAT(altPrice) : 0;
            alternative = {
              price: altPrice * count,
              vat: altVatPerUnit * count,
              total: (altPrice + altVatPerUnit) * count,
            };
          }
          return {
            productId: item.product,
            companyName,
            noOffer: false,
            main: {
              price: mainPrice * count,
              vat: vatPerUnit * count,
              total: (mainPrice + vatPerUnit) * count,
            },
            alternative,
          };
        });
      });

      // Group offers by productId
      const grouped = flatOffers.reduce((acc, offer) => {
        if (!acc[offer.productId]) {
          const product = this.getProductById(offer.productId) || {};
          acc[offer.productId] = {
            Məhsullar: product.name,
            quantity: product.count,
            companies: {}
          };
        }
        acc[offer.productId].companies[offer.companyName] = offer;
        return acc;
      }, {});

      // Optionally, add a total row if needed...
      return Object.values(grouped);
    },
    companies() {
      if (!this.onlyPriceComparisonData.length) return [];
      const [firstRow] = this.onlyPriceComparisonData;
      return Object.keys(firstRow).filter(key => key !== 'Məhsullar');
    },
    rankedOffers() {
      const rankedOffers = this.rankOfferTotals();
      const globalRanking = this.calculateGlobalProductRanking();

      return this.offers.map((offer) => {
        const totals = this.calculateTotals(offer);

        // 2) For each product row in this offer
        const productRanks = this.getPQTPrices(offer).map((item) => {
          const productId = item.product.id || item.product;

          // Find the main entry for this product in globalRanking
          const mainEntry = globalRanking[productId].find(
              (e) => e.offerId === offer.id && e.isAlternative === false
          );
          const mainGlobalRank = mainEntry ? mainEntry.globalRank : null;

          // If there's an alternative, find its rank
          if (item.alternativeOffer) {
            const altEntry = globalRanking[productId].find(
                (e) => e.offerId === offer.id && e.isAlternative === true
            );
            // Attach it directly so your template can use `item.alternativeOffer.globalRank`
            item.alternativeOffer.globalRank = altEntry ? altEntry.globalRank : null;
          }

          // Attach the main rank
          item.globalRank = mainGlobalRank;

          return item;
        });

        return {
          ...offer,

          products: productRanks, // Restore product details
          mainTotal: totals.totalMainPrice,
          alternativeTotal: totals.totalAlternativePrice,
          selectedTotal: totals.totalSelectedPrice,
          mainRank: rankedOffers.find((o) => o.offerId === offer.id)?.mainRank,
          alternativeRank: rankedOffers.find((o) => o.offerId === offer.id)?.alternativeRank,
          offerTechSpecification: this.getParsedPricesExtras(offer).techSpecification, // ← Added this line!
        };
      });
    }

  },

  emits: ['openCompanyRatingStatisticPopup'],
  methods: {
    rankOfferTotals() {
      const offersWithTotals = this.offers.map((offer) => {
        const totals = this.calculateTotals(offer);
        return {
          offerId: offer.id,
          mainTotal: totals.totalMainPrice,
          alternativeTotal: totals.totalAlternativePrice,
          selectedTotal: totals.totalSelectedPrice
        };
      });

      // Only include items with a total > 0
      const combinedTotals = offersWithTotals.flatMap((item) => {
        const arr = [];
        if (item.mainTotal > 0) {
          arr.push({ offerId: item.offerId, total: item.mainTotal, type: 'main' });
        }
        if (item.alternativeTotal > 0) {
          arr.push({ offerId: item.offerId, total: item.alternativeTotal, type: 'alternative' });
        }
        return arr;
      });

      combinedTotals.sort((a, b) => a.total - b.total);
      combinedTotals.forEach((item, index) => {
        item.rank = index + 1;
      });

      offersWithTotals.forEach((offer) => {
        const mainItem = combinedTotals.find(
            (item) => item.offerId === offer.offerId && item.type === 'main'
        );
        offer.mainRank = mainItem ? mainItem.rank : undefined;
        const alternativeItem = combinedTotals.find(
            (item) => item.offerId === offer.offerId && item.type === 'alternative'
        );
        offer.alternativeRank = alternativeItem ? alternativeItem.rank : undefined;
      });

      return offersWithTotals;
    },
    handleCustomOk() {
      this.showOnlyPriceComparison = false; // Optionally close the modal after action
      this.showDetailedView = false;
    },
    getProduct(productId) {
      return this.products.find(product => product.id === productId) || {};
    },

    getParsedPrices(offer) {
      try {
        return JSON.parse(offer.prices).prices || [];
      } catch (error) {
        console.error("Failed to parse prices:", error);
        return [];
      }
    },
    handleRefreshData() {
      // Re-emit the event upward to Request.vue
      this.$emit("refreshData");
    },
    getParsedPricesExtras(offer) {
      try {
        const extras = JSON.parse(offer.prices).extras || {};
        extras.isVatPayer = extras.isVatPayer === 'true'; // Convert to boolean
        return extras;
      } catch (error) {
        console.error("Failed to parse prices extras:", error);
        return {};
      }
    },

    getTechSpecification(offer, pId) {
      return this.getParsedPrices(offer).find(p => p.product === pId);
    },

    getRequirementReply(priceDetail, requirementId) {
      const requirementDetail = priceDetail.reqDetails.find(detail => detail.id === requirementId);
      return requirementDetail ? requirementDetail.detail : 'No reply';
    },
    getCompanyFormat(keyword) {
      return getCompanyFormat(keyword, this.$i18n.locale);
    },

    parsePrices(pricesJSON) {
      try {
        return JSON.parse(pricesJSON);
      } catch (e) {
        return {};
      }
    },

    getPQTPrices(offer) {
      const prices = this.getParsedPrices(offer);

      // Collect product prices for ranking
      const productPrices = prices.map((item) => {
        // Handle noOffer case
        if (item.noOffer) {
          return {
            productId: item.product,
            isVatPayer: item.isVatPayer,
            isSelected: item.isSelected,
            noOffer: true,
            totalPrice: null,
            alternativeTotal: null,
          };
        }

        return {
          productId: item.product,
          isVatPayer: item.isVatPayer,
          isSelected: item.isSelected,
          noOffer: false,
          totalPrice: item.price + (item.isVatPayer === 'true' ? this.calculateVAT(item.price) : 0),
          alternativeTotal: item.alternativeOffer
              ? item.alternativeOffer.price + (item.isVatPayer ? this.calculateVAT(item.alternativeOffer.price) : 0)
              : null,
        };
      });

      // Rank main prices (exclude noOffer products)
      const mainRanked = [...productPrices]
          .filter(item => !item.noOffer)
          .sort((a, b) => a.totalPrice - b.totalPrice);
      mainRanked.forEach((item, index) => (item.rank = index + 1));

      // Rank alternative prices (exclude noOffer products)
      const alternativeRanked = [...productPrices]
          .filter((item) => !item.noOffer && item.alternativeTotal !== null)
          .sort((a, b) => a.alternativeTotal - b.alternativeTotal);
      alternativeRanked.forEach((item, index) => (item.alternativeRank = index + 1));

      return prices.map((item) => {
        const productPrice = productPrices.find((p) => p.productId === item.product);

        // Handle noOffer case
        if (item.noOffer) {
          return {
            product: this.getProductById(item.product),
            noOffer: true,
            price: 0,
            isVatPayer: false,
            isSelected: false,
            vatAmount: 0,
            totalPrice: 0,
            rank: null,
            alternativeOffer: null,
          };
        }

        const vatAmount = item.isVatPayer === 'true' ? this.calculateVAT(item.price) : 0;
        const totalPrice = item.price + vatAmount;
        const isVatPayer = item.isVatPayer;
        let alternativeOffer = null;
        if (item.alternativeOffer) {
          const altVatAmount = item.isVatPayer === 'true' ? this.calculateVAT(item.alternativeOffer.price) : 0;
          alternativeOffer = {
            name: item.alternativeOffer.productName,
            price: item.alternativeOffer.price,
            techSpecs: item.alternativeOffer.techSpecs,
            isVatPayer: item.isVatPayer,
            isSelected: item.alternativeOffer.isSelected,
            vatAmount: altVatAmount,
            total: item.alternativeOffer.price + altVatAmount,
            alternativeRank: productPrice.alternativeRank,
          };
        }

        return {
          product: this.getProductById(item.product),
          noOffer: false,
          price: item.price,
          isVatPayer: isVatPayer === 'true',
          isSelected: item.isSelected,
          vatAmount,
          totalPrice,
          rank: productPrice.rank,
          alternativeOffer,
        };
      });
    },

    calculateGlobalProductRanking() {
      const globalRanking = {};

      // Loop through all offers
      this.offers.forEach((offer) => {
        // getParsedPrices(offer) => array of { product, price, isVatPayer, alternativeOffer?, noOffer? }
        const prices = this.getParsedPrices(offer);

        prices.forEach((item) => {
          const productId = item.product;

          // Initialize array for this product if not exists
          if (!globalRanking[productId]) {
            globalRanking[productId] = [];
          }

          // Skip products with noOffer=true
          if (item.noOffer) {
            // Add a special entry for "no offer" products with a null rank
            globalRanking[productId].push({
              offerId: offer.id,
              isAlternative: false,
              totalPrice: null,
              noOffer: true
            });
            return; // Skip further processing for this item
          }

          // 1) MAIN entry
          const mainTotalPrice =
              item.price + (item.isVatPayer === 'true' ? this.calculateVAT(item.price) : 0);

          // Only include products with price > 0 in ranking
          if (item.price > 0) {
            globalRanking[productId].push({
              offerId: offer.id,
              isAlternative: false,
              totalPrice: mainTotalPrice,
            });
          }

          // 2) ALTERNATIVE entry (if it exists)
          if (item.alternativeOffer) {
            const altPrice = item.alternativeOffer.price;
            const altTotalPrice =
                altPrice + (item.isVatPayer === 'true' ? this.calculateVAT(altPrice) : 0);

            // Only include alternative products with price > 0 in ranking
            if (altPrice > 0) {
              globalRanking[productId].push({
                offerId: offer.id,
                isAlternative: true,
                totalPrice: altTotalPrice,
              });
            }
          }
        });
      });

      // For each product, sort the entries by totalPrice and assign a globalRank using dense ranking.
      for (const productId in globalRanking) {
        // Skip if no entries (all prices were 0)
        if (globalRanking[productId].length === 0) {
          continue;
        }

        // Sort in ascending order (cheapest first)
        globalRanking[productId].sort((a, b) => a.totalPrice - b.totalPrice);

        // Dense ranking: assign same rank to equal prices
        let currentRank = 1;
        globalRanking[productId][0].globalRank = currentRank;

        for (let i = 1; i < globalRanking[productId].length; i++) {
          if (globalRanking[productId][i].totalPrice === globalRanking[productId][i - 1].totalPrice) {
            globalRanking[productId][i].globalRank = currentRank;
          } else {
            currentRank++;
            globalRanking[productId][i].globalRank = currentRank;
          }
        }
      }

      return globalRanking;
    },


    calculateVAT(price) {
      return parseFloat((price * 0.18).toFixed(2)); // 18% VAT
    },
    calculateTotals(offer) {
      const products = this.getPQTPrices(offer);
      // Main total: Sum all main product prices (exclude noOffer products)
      const mainTotal = products.reduce((sum, item) => {
        if (item.noOffer) return sum; // Skip products with noOffer=true
        return sum + ((item.price + item.vatAmount) * item.product.count);
      }, 0);

      // Check if any product has an alternative offer (exclude noOffer products)
      const hasAlternativeOffer = products.some(item => !item.noOffer && item.alternativeOffer);

      // Alternative total: if no alternative offer exists, return 0; otherwise, calculate normally.
      const alternativeTotal = !hasAlternativeOffer
          ? 0
          : products.reduce((sum, item) => {
            if (item.noOffer) return sum; // Skip products with noOffer=true
            if (item.alternativeOffer) {
              return sum + ((item.alternativeOffer.price + item.alternativeOffer.vatAmount) * item.product.count);
            }
            return sum + ((item.price + item.vatAmount) * item.product.count);
          }, 0);

      const selectedTotal = products.reduce((sum, item) => {
        if (item.noOffer) return sum; // Skip products with noOffer=true
        if (item.alternativeOffer && item.alternativeOffer.isSelected) {
          return sum + ((item.alternativeOffer.price + item.alternativeOffer.vatAmount) * item.product.count);
        } else if (item.isSelected) {
          return sum + ((item.price + item.vatAmount) * item.product.count);
        }
        return sum;
      }, 0);

      return {
        totalMainPrice: parseFloat(mainTotal.toFixed(2)),
        totalAlternativePrice: parseFloat(alternativeTotal.toFixed(2)),
        totalSelectedPrice: parseFloat(selectedTotal.toFixed(2))
      };
    },
    getProductById(id) {
      // Always return a product-like object to avoid undefined access (e.g., item.product.count)
      if (id === undefined || id === null) {
        return { id: null, name: '', count: 1, unit: '' };
      }

      const numericId = parseInt(id);
      let prod;
      this.products.forEach(p => {
        if (p.id === numericId) {
          prod = p;
        }
      });

      // Fallback with sensible defaults if product is not found
      return prod || { id: numericId, name: '', count: 1, unit: '' };
    },

    isPhoto(path) {
      return ['jpg', 'png', 'gif', 'jpeg'].indexOf(path.toLowerCase().split('.').pop()) > -1;
    },

    getPhoto(path, size, defaultPhoto) {
      return getPhoto(path, size, defaultPhoto);
    },
    exportResultsOnlyPrice(format) {

      exportRequestResultPrice(this.token, this.request.id, format, this.$i18n.locale).then(data => {
        if (data.code === 200) {
          window.open(mediaBase + data.file);
        } else {
          this.$swal({
            title: `Error ${data.code}`,
            text: `${data.message}`,
            icon: 'error'
          })
        }
      });
    },
    exportResultsDetailedPrice(format) {

      exportRequestResultDetailed(this.token, this.request.id, format, this.$i18n.locale).then(data => {
        if (data.code === 200) {
          window.open(mediaBase + data.file);
        } else {
          this.$swal({
            title: `Error ${data.code}`,
            text: `${data.message}`,
            icon: 'error'
          })
        }
      });

    },

    startConversation(requestUserId) {
      startNewOfferConversation(this.token, this.request.id, requestUserId).then(data => {
        if (data.code === 200) {
          this.$router.push(`/dashboard/conversation/${data.id}`);
        } else {
          this.$swal({
            title: `Error ${data.code}`,
            text: `${data.message}`,
            icon: 'error'
          }).then(() => {
            window.location.reload();
          })
        }

      });
    },

    // Helper method to determine rank badge class based on rank
    getRankClass(rank) {
      if (!rank) return 'rank-none';
      if (rank === 1) return 'rank-first';
      if (rank === 2) return 'rank-second';
      if (rank === 3) return 'rank-third';
      return 'rank-other';
    }
  }
}
</script>
<style>
div.review canvas {
  position: relative !important;
}

div.review input {
  z-index: 1;
  display: inline-block;
}

.offer-container {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.list-unstyled {
  padding-left: 0;
}

.font-weight-bold {
  font-weight: bold;
}

table {
  border-collapse: collapse;
  width: 100%;
}

td,
th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.custom-row-height {
  max-height: 10px;
}

.bold-row {
  font-weight: bold;
}

hr.dashed {
  border-style: dashed;
  border-color: lightblue;
  border-width: 0.1rem;
}

.file-style {
  margin-left: 0.5rem;
  margin-right: 0.5rem;
}

.badge.bg-danger {
  font-size: 0.9rem;
  padding: 0.3em 0.6em;
  border-radius: 0.5rem;
  color: #fff;
  background-color: #dc3545; /* Red */
}

.badge.bg-primary {
  font-size: 0.9rem;
  padding: 0.3em 0.6em;
  border-radius: 0.5rem;
  color: #fff;
  background-color: #007bff; /* Blue */
}

.table th {
  background-color: #e9ecef;
  color: #333;
  text-align: center;
}

.table td {
  text-align: center;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f1f1f1;
  cursor: pointer;
}

.table thead th {
  white-space: nowrap;
}

.col-md-6 strong {
  font-size: 1.1rem;
}

.mt-3 strong {
  font-size: 1rem;
  color: #212529;
}

.table-responsive {
  overflow-x: auto;
  max-width: 100%;
}

@media (max-width: 768px) {
  .table {
    font-size: 0.9rem;
  }

  .badge {
    font-size: 0.8rem;
  }
}

.offer-rejected {
  background-color: #f9f9f9; /* Light gray background */
  opacity: 0.6; /* Make it look 'disabled' */
}

/* Enhanced Product Table Styles */

/* Container for the product table */
.product-table-container {
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 20px;
}

/* Product comparison table */
.product-comparison-table {
  table-layout: fixed;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #e0e0e0;
}

.product-comparison-table thead th {
  background-color: #f5f7fa;
  color: #495057;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  padding: 12px 15px;
  border-bottom: 2px solid #dee2e6;
  text-align: center;
}

.product-comparison-table tbody tr {
  transition: background-color 0.2s ease;
}

.product-comparison-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Column widths */
.product-col { width: 30%; }
.price-col { width: 20%; }
.vat-col { width: 15%; }
.total-col { width: 20%; }
.rank-col { width: 15%; }

/* Product name styling */
.product-name {
  padding: 15px;
  text-align: left;
}

.product-name-container {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name-container strong {
  font-size: 1rem;
  color: #333;
  display: block;
  word-break: break-word;
}

/* Best price badge */
.best-price-badge {
  display: inline-block;
  background-color: #28a745;
  color: white;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  margin-top: 4px;
  align-self: center;
}

/* Price value styling */
.price-value, .vat-value, .total-value, .rank-value {
  padding: 15px;
  text-align: center;
  vertical-align: middle;
}

.price-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.price-amount {
  font-weight: 600;
  color: #333;
}

.price-quantity {
  font-size: 0.85rem;
  color: #6c757d;
}

.vat-amount, .total-amount {
  font-weight: 500;
}

/* Rank badge styling */
.rank-badge {
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.9rem;
}

.rank-first {
  background-color: #28A745;
  color: white;
}

.rank-second {
  background-color: #FFC107;
  color: white;
}

.rank-third {
  background-color: #DC3545;
  color: white;
}

.rank-other {
  background-color: #e9ecef;
  color: #495057;
}

.rank-none {
  background-color: transparent;
  color: #6c757d;
}

/* Alternative row styling */
.alternative-row {
  background-color: #f8f9ff; /* Light blue tint */
}

.alternative-row:hover {
  background-color: #f0f4ff;
}

.alternative-label {
  display: inline-block;
  background-color: #007bff;
  color: white;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  margin-bottom: 4px;
  align-self: center;
}

.alternative-name {
  font-size: 0.9rem;
  color: #007bff;
  font-style: italic;
}

/* Best rank row highlight */
.best-rank {
  background-color: rgba(40, 167, 69, 0.05);
}

.best-rank:hover {
  background-color: rgba(40, 167, 69, 0.1);
}

/* Offer Totals Styling */
.offer-totals-container {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.offer-total-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e9ecef;
}

.offer-total-row:last-child {
  border-bottom: none;
}

.offer-total-row.alternative {
  margin-top: 10px;
  background-color: #f8f9ff;
  border-radius: 6px;
  padding: 10px 0;
}

.offer-total-label {
  flex: 2;
  font-size: 1rem;
}

.offer-total-amount {
  flex: 1;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  text-align: right;
  padding-right: 20px;
}

.offer-total-rank {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.total-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-weight: bold;
  font-size: 1rem;
  margin-right: 8px;
}

.rank-label {
  font-size: 0.9rem;
  color: #6c757d;
}

/* No Offer Styling */
.no-offer-row {
  background-color: #f8f9fa;
  opacity: 0.8;
}

.no-offer-badge {
  display: inline-block;
  background-color: #6c757d;
  color: white;
  font-size: 0.85rem;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 500;
}

.no-offer-rank {
  display: inline-block;
  width: 30px;
  height: 30px;
  line-height: 30px;
  text-align: center;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  font-weight: bold;
  font-size: 0.9rem;
}

/* Company Info Section Styling */
.company-info-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px 10px;
  margin-bottom: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.company-details {
  flex: 2;
}

.company-name-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.company-name {
  font-size: 1.4rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.rejected-badge {
  display: inline-block;
  background-color: #dc3545;
  color: white;
  font-size: 0.8rem;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: normal;
}

.rating-container {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.enhanced-star-rating {
  display: inline-flex;
  align-items: center;
}

.enhanced-star-rating i {
  font-size: 1.1rem;
  margin-right: 2px;
}

.enhanced-star-rating i.fa-star {
  color: #ffc107;
}

.enhanced-star-rating i.fa-star-o {
  color: #ccc;
}

.rating-info {
  font-size: 0.85rem;
  color: #6c757d;
}

.price-summary {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
}

.total-price-badge {
  display: inline-block;
  background-color: #007bff;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 8px 15px;
  border-radius: 6px;
  margin-bottom: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.total-label {
  font-size: 0.9rem;
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .product-comparison-table {
    font-size: 0.9rem;
  }

  .product-name-container strong {
    font-size: 0.9rem;
  }

  .price-amount, .vat-amount, .total-amount {
    font-size: 0.9rem;
  }

  .offer-total-row {
    flex-direction: column;
    align-items: flex-start;
    padding: 15px 0;
  }

  .offer-total-label, .offer-total-amount, .offer-total-rank {
    width: 100%;
    text-align: left;
    margin-bottom: 8px;
  }

  .offer-total-rank {
    justify-content: flex-start;
  }

  .company-info-section {
    flex-direction: column;
    gap: 15px;
  }

  .price-summary {
    align-items: flex-start;
    width: 100%;
  }

  .company-name {
    font-size: 1.2rem;
  }

  .total-price-badge {
    font-size: 1rem;
  }
}

@media (max-width: 768px) {
  .product-comparison-table thead th {
    font-size: 0.8rem;
    padding: 10px;
  }

  .product-name, .price-value, .vat-value, .total-value, .rank-value {
    padding: 10px;
  }

  .rank-badge, .total-rank-badge {
    width: 25px;
    height: 25px;
    line-height: 25px;
    font-size: 0.8rem;
  }

  .offer-totals-container {
    padding: 10px;
  }
}
</style>
